import { useState } from 'react';
import { X, UserPlus, UserMinus } from 'lucide-react';
import {
  useChannelMembers,
  useAddChannelMember,
  useRemoveChannelMember,
  useCollaboratorsForMessaging,
  type Channel,
} from '../../hooks/queries/useMessaging';
import { useAuthStore } from '../../store/useAuthStore';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { CollaboratorSelect } from '../collaborators/CollaboratorSelect';
import { Avatar } from '../ui/Avatar';

interface ChannelMembersModalProps {
  channel: Channel;
  onClose: () => void;
}

export function ChannelMembersModal({ channel, onClose }: ChannelMembersModalProps) {
  const [selectedId, setSelectedId] = useState('');
  const collaborator = useAuthStore((s) => s.collaborator);

  const { data: members = [], isLoading: membersLoading } = useChannelMembers(channel.id);
  const { data: allCollaborators = [] } = useCollaboratorsForMessaging();
  const addMember = useAddChannelMember();
  const removeMember = useRemoveChannelMember();

  const memberIds = new Set(members.map((m) => m.id));
  // Include current user in exclusion set
  if (collaborator?.id) memberIds.add(collaborator.id);
  const available = allCollaborators.filter((c) => !memberIds.has(c.id));

  function handleAdd() {
    if (!selectedId) return;
    addMember.mutate(
      { channelId: channel.id, collaboratorId: selectedId },
      { onSuccess: () => setSelectedId('') },
    );
  }

  function handleRemove(collaboratorId: string) {
    removeMember.mutate({ channelId: channel.id, collaboratorId });
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-cime-surface rounded-xl shadow-xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-cime-border">
          <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
            Membres — #{channel.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-cime-sidebar-active-bg text-cime-text-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Ajouter un membre */}
          <div>
            <p className="text-xs font-medium text-cime-text-muted mb-2">Ajouter un membre</p>
            {available.length === 0 ? (
              <p className="text-xs text-cime-text-muted italic">Tous les collaborateurs sont déjà membres.</p>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <CollaboratorSelect
                    options={available}
                    value={selectedId}
                    onChange={setSelectedId}
                    placeholder="Sélectionner…"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!selectedId || addMember.isPending}
                  className="p-2 rounded-lg bg-cime-primary text-white hover:bg-cime-primary/90 disabled:opacity-50 transition-colors"
                  title="Ajouter"
                >
                  <UserPlus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Liste membres */}
          <div>
            <p className="text-xs font-medium text-cime-text-muted mb-2">Membres actuels</p>
            {membersLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-8 bg-cime-bg rounded-lg animate-pulse" />
                ))}
              </div>
            ) : members.length === 0 ? (
              <p className="text-xs text-cime-text-muted italic">Aucun membre pour l'instant.</p>
            ) : (
              <ul className="max-h-56 overflow-y-auto space-y-1">
                {members.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-cime-sidebar-active-bg group"
                  >
                    <CollaboratorLink collaboratorId={m.id} title={`Voir le profil de ${m.full_name}`}>
                      <Avatar name={m.full_name} src={m.avatar_url} size="xs" />
                    </CollaboratorLink>
                    <CollaboratorLink collaboratorId={m.id} title={`Voir le profil de ${m.full_name}`}>
                      <span className="flex-1 text-sm text-cime-text-primary truncate">{m.full_name}</span>
                    </CollaboratorLink>
                    <button
                      type="button"
                      onClick={() => handleRemove(m.id)}
                      disabled={removeMember.isPending}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-cime-text-muted hover:text-red-500 hover:bg-red-50 transition-all"
                      title="Retirer"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
